import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

import { FuseConfirmationService } from '@fuse/services/confirmation';
import { BusinessPartnerNavigationService } from 'app/shared/services/business-partner-navigation.service';

import { AssignTerritoryModalService } from './assign-territory-modal.service';
import { TerritoryStepComponent } from './territory-step/territory-step.component';
import { AddressStepComponent } from './address-step/address-step.component';
import {
  AssignTerritoryModalData,
  AssignTerritoryModalResult,
} from './interfaces/assign-territory-modal.interface';

@Component({
  selector: 'app-assign-territory-modal',
  templateUrl: './assign-territory-modal.component.html',
  styleUrls: ['./assign-territory-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatStepperModule,
    TerritoryStepComponent,
    AddressStepComponent,
  ],
  providers: [AssignTerritoryModalService],
})
export class AssignTerritoryModalComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;

  currentStepIndex = signal(0);

  constructor(
    public dialogRef: MatDialogRef<AssignTerritoryModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssignTerritoryModalData,
    public readonly modalService: AssignTerritoryModalService,
    private readonly bpNavigationService: BusinessPartnerNavigationService,
    private readonly fuseConfirmationService: FuseConfirmationService
  ) {}

  ngOnInit(): void {
    this.modalService.loadBusinessPartner(this.data.clientId);
  }

  ngOnDestroy(): void {
    this.modalService.destroy();
  }

  // === STEPPER NAVIGATION ===

  goToAddressStep(): void {
    if (this.modalService.canProceedToAddress()) {
      this.stepper.next();
      this.currentStepIndex.set(1);
    }
  }

  goToTerritoriesStep(): void {
    this.stepper.previous();
    this.currentStepIndex.set(0);
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.currentStepIndex.set(event.selectedIndex);
  }

  // === EXTERNAL NAVIGATION ===

  navigateToClient(): void {
    const bp = this.modalService.businessPartner();
    if (!bp) return;

    this.bpNavigationService.navigateToBusinessPartnerDetail(
      {
        id: bp._id?.toString(),
        code: bp.externalKeys?.code,
        name: bp.name,
        externalKeys: bp.externalKeys,
      },
      {},
      true
    );
  }

  // === SAVE ===

  save(): void {
    const territoryCount = this.modalService.selectedTerritoriesCount();
    const addressCount = this.modalService.selectedAddressesCount();

    const confirmation = this.fuseConfirmationService.open({
      title: 'Confirmar Asignación',
      message: `¿Está seguro de asignar ${territoryCount} territorio(s) a ${addressCount} dirección(es) seleccionada(s)?`,
      icon: {
        show: true,
        name: 'heroicons_outline:map',
        color: 'primary',
      },
      actions: {
        confirm: {
          label: 'Asignar',
          color: 'primary',
        },
        cancel: {
          label: 'Cancelar',
        },
      },
    });

    confirmation.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.modalService.performSave().subscribe(res => {
          if (res.success) {
            this.dialogRef.close(res);
          }
        });
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
